// =============================================================================
// Optim TURPE V2 — LA FENÊTRE D'ANALYSE ET SES LIMITES (DEV-1913, L9).
//
// Décision de Mathieu du 23/09 : « l'utilisateur doit pouvoir décider de lancer
// sur des fenêtres plus petites, on lui précise toutes les limites ». La page
// n'impose donc aucun minimum au-delà de deux jours ; elle DIT, avant le clic,
// ce que la fenêtre choisie ne permettra pas de voir.
//
// Retours d'usage du 22/10/2026 : ce que le moteur refuse À COUP SÛR ne part
// plus. Deux règles du moteur sont recopiées ici, une seule fois, faute d'un
// pré-contrôle de la période appelable avant le lancement (`/optimturpe/perimetre`
// ne sert rien de la fenêtre, lu sur courtage origin/main 48bce74) :
// l'ordre des dates (`JOURS_MINIMUM_FENETRE`) et les postes absents d'un C4 ou
// d'un C2 (`postesAbsentsACoupSur`). La table `optim-turpe-v2.fenetre.cas.json`
// fige les cas, avec le refus que le moteur rend pour chacun.
//
// Deux règles tenues ici :
// 1. **On n'affirme que des négatifs certains.** « Cette classe ne sera pas
//    observée » se prouve : aucun de ses mois n'est dans la fenêtre. L'inverse
//    (« elle sera observée ») ne se prouve pas depuis des dates, puisqu'un mois
//    touché d'un seul jour ne garantit aucune mesure. La page ne l'écrit jamais.
// 2. **Aucune bannière.** Ces phrases vivent sous le sélecteur de période, à
//    l'endroit même où l'on choisit la fenêtre.
// =============================================================================

import { LIBELLE_CLASSE_HORAIRE } from "@modules/courtage/utils/optim-turpe-v2.libelles"

/**
 * Le seul minimum : deux jours, bornes incluses. Reflète courtage
 * `modules/optimturpe/fenetre.py:64-65` (`valider_fenetre` :
 * `if debut >= fin: raise runerror("validation-fenetre-invalide")`) : le moteur
 * exige un début STRICTEMENT antérieur à la fin, et refuse donc une période d'un
 * seul jour. Constante nommée pour qu'un futur « et si on imposait trente
 * jours » se voie, au lieu de revenir en silence (L9).
 */
export const JOURS_MINIMUM_FENETRE = 2

/**
 * Le maximum servi par le moteur (`fenetre.py`, décision L10 : trois ans).
 * La page le recopie ici pour refuser AVANT l'appel plutôt que de faire faire
 * l'aller-retour au client.
 */
export const JOURS_MAXIMUM_FENETRE = 1096

/**
 * Le seuil au-dessus duquel le moteur sert un équivalent annuel
 * (`ANNUALISATION_JOURS_MIN`, Q-CIBLE-8, confirmé par L9). En dessous, le
 * montant de période reste servi et l'équivalent annuel est `null` avec sa cause.
 */
export const JOURS_POUR_EQUIVALENT_ANNUEL = 335

/**
 * Le calendrier horosaisonnier du TURPE, **proposition à valider** (texte-ok).
 *
 * Il ne sert QU'À annoncer une limite avant le lancement. Aucun chiffre de
 * l'étude n'en dépend : le découpage qui fait foi est celui du moteur et du
 * pricer, jamais celui-ci.
 */
export const MOIS_SAISON_HAUTE = [11, 12, 1, 2, 3]
export const MOIS_SAISON_BASSE = [4, 5, 6, 7, 8, 9, 10]
export const MOIS_POINTE_FIXE = [12, 1, 2]

/** Les classes temporelles du TURPE, et les mois où chacune peut se produire. */
const MOIS_PAR_CLASSE: Array<{ classe: string; mois: number[] }> = [
    { classe: "PTE", mois: MOIS_POINTE_FIXE },
    { classe: "HPH", mois: MOIS_SAISON_HAUTE },
    { classe: "HCH", mois: MOIS_SAISON_HAUTE },
    { classe: "HPB", mois: MOIS_SAISON_BASSE },
    { classe: "HCB", mois: MOIS_SAISON_BASSE },
]

export type FenetreCauseInvalide =
    | "dates-absentes"
    /** La fin précède le début, ou lui est égale (période d'un seul jour). */
    | "fin-avant-debut"
    | "trop-longue"

export type FenetreLue = {
    /** Nombre de jours couverts, bornes incluses. `null` si une date manque. */
    jours: number | null
    valide: boolean
    causeInvalide: FenetreCauseInvalide | null
    /** L'équivalent annuel sera-t-il servi sur cette fenêtre ? */
    equivalentAnnuelServi: boolean
    /** Les mois de l'année (1 à 12) qu'aucun jour de la fenêtre ne touche. */
    moisNonCouverts: number[]
    /** Les saisons tarifaires dont aucun mois n'est dans la fenêtre. */
    saisonsNonObservees: string[]
    /** Les classes temporelles dont aucun mois n'est dans la fenêtre. */
    classesNonObservees: string[]
    /** Les phrases à montrer sous le sélecteur, avant le lancement. */
    limites: string[]
}

const MILLISECONDES_PAR_JOUR = 24 * 60 * 60 * 1000

function jourUTC(date: string): number | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
    const valeur = Date.parse(`${date}T00:00:00Z`)
    return Number.isFinite(valeur) ? valeur : null
}

function vide(cause: FenetreCauseInvalide, jours: number | null): FenetreLue {
    return {
        jours,
        valide: false,
        causeInvalide: cause,
        equivalentAnnuelServi: false,
        moisNonCouverts: [],
        saisonsNonObservees: [],
        classesNonObservees: [],
        limites: [],
    }
}

/** Les mois (1 à 12) touchés par au moins un jour de la fenêtre. */
function moisCouverts(debut: number, fin: number): Set<number> {
    const couverts = new Set<number>()
    const curseur = new Date(debut)
    const borne = new Date(fin)
    // Au-delà d'une année pleine, les douze mois sont forcément touchés : on
    // sort tôt plutôt que d'itérer trois ans de mois pour rien.
    while (curseur.getTime() <= borne.getTime() && couverts.size < 12) {
        couverts.add(curseur.getUTCMonth() + 1)
        curseur.setUTCMonth(curseur.getUTCMonth() + 1, 1)
    }
    couverts.add(borne.getUTCMonth() + 1)
    return couverts
}

/**
 * La fenêtre demandée, avec ce qu'elle ne permettra pas de voir.
 *
 * Aucune donnée de l'étude n'est lue ici : ce sont les DATES qui parlent, donc
 * la lecture vaut avant le lancement comme après, sur la fenêtre réellement
 * analysée.
 */
export function lireFenetre(
    dateFrom: string | null | undefined,
    dateTo: string | null | undefined
): FenetreLue {
    const debut = dateFrom ? jourUTC(dateFrom) : null
    const fin = dateTo ? jourUTC(dateTo) : null
    if (debut === null || fin === null) return vide("dates-absentes", null)

    const jours = Math.round((fin - debut) / MILLISECONDES_PAR_JOUR) + 1
    if (jours < JOURS_MINIMUM_FENETRE) return vide("fin-avant-debut", jours)
    if (jours > JOURS_MAXIMUM_FENETRE) return vide("trop-longue", jours)

    const couverts = moisCouverts(debut, fin)
    const moisNonCouverts = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].filter(
        (mois) => !couverts.has(mois)
    )
    const classesNonObservees = MOIS_PAR_CLASSE.filter(
        ({ mois }) => !mois.some((m) => couverts.has(m))
    ).map(({ classe }) => classe)
    const saisonsNonObservees = [
        { nom: "saison haute", mois: MOIS_SAISON_HAUTE },
        { nom: "saison basse", mois: MOIS_SAISON_BASSE },
    ]
        .filter(({ mois }) => !mois.some((m) => couverts.has(m)))
        .map(({ nom }) => nom)

    const equivalentAnnuelServi = jours >= JOURS_POUR_EQUIVALENT_ANNUEL
    const limites: string[] = []
    if (!equivalentAnnuelServi) {
        limites.push(
            `Cette période couvre ${jours} jour${jours > 1 ? "s" : ""} : sous ${JOURS_POUR_EQUIVALENT_ANNUEL} jours, l'équivalent annuel n'est pas calculé. Le montant de la période reste affiché.`
        )
    }
    if (saisonsNonObservees.length > 0) {
        limites.push(
            `${saisonsNonObservees.join(" et ")} ne sera pas observée sur cette période : la recommandation ne tient compte que de ce qui s'y est passé.`
        )
    }
    if (classesNonObservees.length > 0) {
        limites.push(
            // Revue témoin du 15/10/2026 (D23, Q6) : un poste, jamais une plage.
            classesNonObservees.length === 1
                ? `Le poste ${classesNonObservees[0]} ne sera pas observé : aucune puissance ne sera mesurée pour lui.`
                : `Les postes ${classesNonObservees.join(", ")} ne seront pas observés : aucune puissance ne sera mesurée pour eux.`
        )
    }

    return {
        jours,
        valide: true,
        causeInvalide: null,
        equivalentAnnuelServi,
        moisNonCouverts,
        saisonsNonObservees,
        classesNonObservees,
        limites,
    }
}

// =============================================================================
// LES POSTES QU'UNE PÉRIODE NE MESURE PAS, EN C4 ET EN C2 (retours du 22/10).
//
// Reflète courtage `modules/optimturpe/classes_hors_fenetre.py:30-34` (postes
// requis HPH, HCH, HPB, HCB ; grilles des seuls C4 et C2) et `:65-81`
// (`raise_si_classe_requise_absente`, refus `donnee-classe-hors-fenetre`), appelé
// par `optimturpe_10min_calculette.py:369`. La saison d'un pas se lit au mois,
// à l'heure de Paris (pricer `categorisation_SHSB` : avril à octobre en saison
// basse, le reste en saison haute). La pointe reste facultative ; le C5 n'est
// pas concerné.
// =============================================================================

/** Les postes sans lesquels le moteur refuse l'étude, par segment normalisé. */
export const POSTES_REQUIS_PAR_SEGMENT: Record<string, string[]> = {
    C4: ["HPH", "HCH", "HPB", "HCB"],
    C2: ["HPH", "HCH", "HPB", "HCB"],
}

export type PostesAbsents = {
    /** La saison dont aucun jour n'est dans la période. */
    saison: "saison haute" | "saison basse"
    /** Les postes requis qui n'y seront pas mesurés, dans l'ordre du moteur. */
    postes: string[]
}

/**
 * Les postes requis qu'une période valide ne mesurera À COUP SÛR pas, ou `null`.
 *
 * Négatif certain seulement : la période est élargie d'un jour de chaque côté
 * avant de lire ses mois. Le moteur classe chaque pas à l'heure de Paris, et la
 * borne horaire du chargeur n'est pas recopiée ici ; une période qui s'arrête le
 * 31 octobre pourrait donc toucher une heure de saison haute. Dans ce cas limite,
 * la page prévient (limite orange) sans bloquer.
 */
export function postesAbsentsACoupSur(
    dateFrom: string | null | undefined,
    dateTo: string | null | undefined,
    segment: string | null | undefined
): PostesAbsents | null {
    const requis = POSTES_REQUIS_PAR_SEGMENT[segment ?? ""]
    if (!requis) return null
    if (!lireFenetre(dateFrom, dateTo).valide) return null
    const debut = jourUTC(dateFrom as string) as number
    const fin = jourUTC(dateTo as string) as number
    const couverts = moisCouverts(
        debut - MILLISECONDES_PAR_JOUR,
        fin + MILLISECONDES_PAR_JOUR
    )
    const absents = MOIS_PAR_CLASSE.filter(
        ({ classe, mois }) =>
            requis.includes(classe) && !mois.some((m) => couverts.has(m))
    ).map(({ classe }) => classe)
    if (absents.length === 0) return null
    return {
        saison: absents.includes("HPH") ? "saison haute" : "saison basse",
        postes: absents,
    }
}

/**
 * Les postes absents qui font REFUSER l'étude, ou `null`.
 *
 * Reflète courtage `optimturpe_10min_calculette.py:368` : le refus des postes
 * absents ne s'applique qu'hors du mode « prendre la main » (`if not
 * is_price_only`). Dans ce mode, la structure imposée est chiffrée sans refus ;
 * la page prévient (limite orange) sans bloquer.
 */
export function postesAbsentsRefuses(
    dateFrom: string | null | undefined,
    dateTo: string | null | undefined,
    segment: string | null | undefined,
    mode: string | null | undefined
): PostesAbsents | null {
    if (mode === "price_only") return null
    return postesAbsentsACoupSur(dateFrom, dateTo, segment)
}

/**
 * La phrase du refus certain des postes absents (paramètres `{saison}` et
 * `{postes}`, composés par `textesDesPostes`). Une seule source : le panneau
 * « Affiner l'étude » et le bouton qui relance sur la période proposée la
 * disent tous deux (retours du 23/10/2026, Optim TURPE V2).
 */
export const MESSAGE_REFUS_POSTES =
    "Cette période ne contient aucun jour de {saison} : les postes {postes} n'y sont pas mesurés, et l'étude serait refusée, car les puissances souscrites se chiffrent sur tous les postes. Choisissez dans le champ Période une période qui couvre les deux saisons, par exemple les douze derniers mois."

/** Les postes avec leur nom et leur code, et la saison avec ses mois. */
export function textesDesPostes(postes: string[]): {
    saison: string
    postes: string
} {
    const noms = postes.map(
        (code) => `${LIBELLE_CLASSE_HORAIRE[code] ?? code} (${code})`
    )
    const saison = postes.includes("HPH")
        ? "saison haute (novembre à mars)"
        : "saison basse (avril à octobre)"
    return {
        saison,
        postes:
            noms.length > 1
                ? `${noms.slice(0, -1).join(", ")} et ${noms[noms.length - 1]}`
                : noms.join(""),
    }
}

// =============================================================================
// LA PÉRIODE PROPOSÉE APRÈS UN REFUS SUR LA PÉRIODE (DEV-2164, cas 1 et 2).
//
// Ce que le client doit vivre : après un refus qui tient à la période, il ne
// cherche pas lui-même quelle période marcherait. La page la lui propose en un
// geste, et dit d'où elle vient : servie par le moteur, ou déduite par la page.
// =============================================================================

export type PeriodeProposee = { date_from: string; date_to: string }

const DATE_ISO = /^(\d{4})-(\d{2})-(\d{2})$/

function iso(t: number): string {
    return new Date(t).toISOString().slice(0, 10)
}

/**
 * L'année complète qui se termine le `dateTo` inclus (« Étendre à l'année »).
 * Comptée au calendrier : du lendemain de la même date un an plus tôt.
 */
export function anneeSeTerminantLe(dateTo: string): PeriodeProposee | null {
    const m = DATE_ISO.exec(dateTo ?? "")
    if (!m) return null
    const annee = Number(m[1])
    const mois = Number(m[2]) - 1
    const jour = Number(m[3])
    const fin = Date.UTC(annee, mois, jour)
    if (Number.isNaN(fin) || iso(fin) !== dateTo) return null
    const joursDuMois = new Date(Date.UTC(annee - 1, mois + 1, 0)).getUTCDate()
    const unAnAvant = Date.UTC(annee - 1, mois, Math.min(jour, joursDuMois))
    return { date_from: iso(unAnAvant + 86_400_000), date_to: dateTo }
}

/**
 * La meilleure période disponible après le refus « collecte interrompue ».
 *
 * `qualite_donnees.meilleure_periode` passe toujours en premier quand le moteur
 * la sert. Relevé en production le 25/09/2026 : un refus de ce type ne la porte
 * PAS (le document n'a pas d'`outputs`). À défaut, la page propose l'année qui se
 * termine à la date de dernière donnée nommée par le refus (« interrompue depuis
 * le JJ/MM/AAAA »), et la dit DÉDUITE. Sans date lisible, rien n'est proposé.
 */
export function periodeApresCollecteInterrompue(
    message: string | null | undefined,
    servie: PeriodeProposee | null | undefined
): { periode: PeriodeProposee; servie: boolean } | null {
    if (servie?.date_from && servie?.date_to) {
        return {
            periode: { date_from: servie.date_from, date_to: servie.date_to },
            servie: true,
        }
    }
    const m = /depuis le (\d{2})\/(\d{2})\/(\d{4})/.exec(message ?? "")
    if (!m) return null
    const periode = anneeSeTerminantLe(`${m[3]}-${m[2]}-${m[1]}`)
    return periode ? { periode, servie: false } : null
}
